import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { CurrencyInr, GoogleLogo, ShieldCheck, ChartLineUp, Receipt, Envelope, LockKey } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState("choice"); // "choice" | "signin" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const location = useLocation();
  const authError = location.state?.authError;

  useEffect(() => {
    // If returning from OAuth flow, AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        await api.get("/auth/me");
        const next = new URLSearchParams(window.location.search).get("next");
        navigate(next || "/dashboard", { replace: true });
      } catch {
        setChecking(false);
      }
    })();
  }, [navigate]);

  const handleGoogleLogin = () => {
    // Persist deep-link target before bouncing through OAuth (the OAuth
    // redirect_uri must stay constant or the flow breaks).
    const next = new URLSearchParams(window.location.search).get("next");
    if (next) {
      try { sessionStorage.setItem("login_next", next); } catch {}
    }
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const formatError = (e) => {
    const d = e?.response?.data?.detail;
    if (!d) return e?.message || "Something went wrong.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
    return String(d);
  };

  const submitPasswordForm = async (endpoint) => {
    setErr("");
    setBusy(true);
    try {
      const body = endpoint === "register"
        ? { email: email.trim(), password, name: name.trim() }
        : { email: email.trim(), password };
      const { data } = await api.post(`/auth/${endpoint}`, body);
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next || "/dashboard", { replace: true });
      return data;
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-zinc-50" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="lg:col-span-3 relative overflow-hidden border-r border-zinc-200 bg-white">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative h-full flex flex-col p-10 lg:p-16">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center rounded-sm bg-gradient-to-br from-red-500 to-red-700 shadow-[0_0_18px_rgba(239,68,68,0.45)]" data-testid="brand-mark">
              <CurrencyInr size={22} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="font-heading font-black text-lg leading-none tracking-tight brand-gradient">RECONIK</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Finance Control Room</div>
            </div>
          </div>

          <div className="mt-20 lg:mt-32 max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.3em] text-blue-600 font-bold mb-6">Daily cash command</div>
            <h1 className="font-heading font-black tracking-tight text-4xl lg:text-6xl leading-[1.05] text-zinc-900">
              One screen.<br />Every store.<br />
              <span className="text-blue-600">Cash truth.</span>
            </h1>
            <p className="mt-8 text-zinc-600 text-base leading-relaxed max-w-lg">
              Approvals, reconciliation, statutory alerts and cash health — across all outlets.
              Built for daily finance ops, not quarterly reports.
            </p>
          </div>

          <div className="mt-auto grid grid-cols-3 gap-8 pt-12 max-w-2xl">
            <Feature icon={<Receipt size={20} weight="duotone" />} label="Approvals" sub="Variance flagged" />
            <Feature icon={<ShieldCheck size={20} weight="duotone" />} label="Statutory" sub="GST · TDS · PF" />
            <Feature icon={<ChartLineUp size={20} weight="duotone" />} label="Cash Health" sub="7-day horizon" />
          </div>
        </div>
      </div>

      {/* Right: login */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            {mode === "signup" ? "Create account" : "Sign in"}
          </div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-zinc-900">
            {mode === "signup" ? "Set up your access" : "Access the control room"}
          </h2>
          {authError && (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3 leading-relaxed" data-testid="login-not-authorised">
              {authError}
            </div>
          )}
          <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
            {mode === "signup"
              ? "Choose a password to sign in without needing Google every time."
              : "Sign in with Google or your email + password. Sessions persist for 7 days."}
          </p>

          {mode === "choice" && (
            <>
              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                className="mt-8 w-full h-12 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm gap-3 text-sm font-semibold"
              >
                <GoogleLogo size={18} weight="bold" />
                Continue with Google
              </Button>

              <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                <div className="flex-1 h-px bg-zinc-200" />
                or
                <div className="flex-1 h-px bg-zinc-200" />
              </div>

              <Button
                data-testid="password-login-toggle-btn"
                onClick={() => { setErr(""); setMode("signin"); }}
                variant="outline"
                className="mt-6 w-full h-12 border-zinc-300 hover:bg-zinc-100 text-zinc-900 rounded-sm gap-3 text-sm font-semibold"
              >
                <Envelope size={18} weight="bold" />
                Sign in with email &amp; password
              </Button>
            </>
          )}

          {(mode === "signin" || mode === "signup") && (
            <form
              className="mt-8 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                submitPasswordForm(mode === "signup" ? "register" : "login");
              }}
              data-testid={`password-form-${mode}`}
            >
              {mode === "signup" && (
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Name</Label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    autoComplete="name"
                    className="mt-1 h-11 rounded-sm border-zinc-300 focus:ring-2 focus:ring-blue-500"
                    data-testid="signup-name-input"
                  />
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold flex items-center gap-1.5">
                  <Envelope size={11} weight="bold" /> Email
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  autoComplete={mode === "signup" ? "email" : "username"}
                  required
                  className="mt-1 h-11 rounded-sm border-zinc-300 focus:ring-2 focus:ring-blue-500"
                  data-testid={`${mode}-email-input`}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold flex items-center gap-1.5">
                  <LockKey size={11} weight="bold" /> Password
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "At least 8 characters" : ""}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  required
                  className="mt-1 h-11 rounded-sm border-zinc-300 focus:ring-2 focus:ring-blue-500"
                  data-testid={`${mode}-password-input`}
                />
              </div>

              {err && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm p-2.5 leading-relaxed" data-testid="password-form-error">
                  {err}
                </div>
              )}

              <Button
                type="submit"
                disabled={busy}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-sm text-sm font-semibold"
                data-testid={`${mode}-submit-btn`}
              >
                {busy ? "Signing in…" : mode === "signup" ? "Create account" : "Sign in"}
              </Button>

              <div className="flex items-center justify-between text-xs text-zinc-500 pt-2">
                <button
                  type="button"
                  onClick={() => { setErr(""); setMode("choice"); }}
                  className="hover:text-zinc-900 underline underline-offset-2"
                  data-testid="password-back-btn"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={() => { setErr(""); setMode(mode === "signup" ? "signin" : "signup"); }}
                  className="hover:text-zinc-900 underline underline-offset-2"
                  data-testid="password-mode-toggle-btn"
                >
                  {mode === "signup" ? "Have an account? Sign in" : "Need an account? Create one"}
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-zinc-200 text-[11px] text-zinc-500 leading-relaxed">
            Internal tool. Access restricted to authorized finance and director accounts.
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, label, sub }) {
  return (
    <div className="border-l border-zinc-200 pl-4">
      <div className="text-zinc-900 mb-2">{icon}</div>
      <div className="text-sm font-semibold text-zinc-900">{label}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}
